import {DocumentNode} from '@apollo/client';
import {useQuery} from '@apollo/client';
import * as React from 'react';
import {Link} from 'react-router';

import {PER_PAGE, mesclarPorId, paginaCheia} from '../paginacao';
import {StudentTheme} from '../studentTheme';
import {TargetItem, capaDoAlvo, dataDeConclusao, iconeDoTipo, rotaDoAlvo, rotuloDoAlvo, tipoLegivel} from '../targetList';

interface Props {
  theme: StudentTheme;
  query: DocumentNode;
  /** Nome do campo raiz na resposta: favorites ou userCompletions. */
  campo: string;
  vazio: string;
  erro: string;
}

interface Pagina {
  total?: number | null;
  list?: TargetItem[] | null;
}

// Favoritos e Completados tem a mesma forma — { total, list: [{ target }] } —
// e so mudam a query e os textos. Uma tela parametrizada evita duplicar a
// paginacao e o tratamento de alvo sem titulo nas duas.
export const TargetListScreen: React.FC<Props> = ({theme, query, campo, vazio, erro}) => {
  const {data, loading, error, fetchMore, refetch} = useQuery<Record<string, Pagina>>(query, {
    variables: {page: 1},
  });

  const [pagina, setPagina] = React.useState(1);
  const [carregandoMais, setCarregandoMais] = React.useState(false);
  const [acabou, setAcabou] = React.useState(false);

  const itens = data?.[campo]?.list ?? [];
  const total = data?.[campo]?.total ?? 0;

  const carregarMais = async () => {
    if (carregandoMais || acabou) return;

    const proxima = pagina + 1;
    setCarregandoMais(true);

    try {
      const {data: novos} = await fetchMore({
        variables: {page: proxima},
        updateQuery: (anterior, {fetchMoreResult}) => {
          const novaLista = fetchMoreResult?.[campo]?.list ?? [];
          if (novaLista.length === 0) return anterior;

          return {
            ...anterior,
            [campo]: {
              ...anterior?.[campo],
              list: mesclarPorId(anterior?.[campo]?.list ?? [], novaLista),
            },
          };
        },
      });

      setPagina(proxima);
      if (!paginaCheia(novos?.[campo]?.list ?? [], PER_PAGE)) setAcabou(true);
    } catch {
      setAcabou(true);
    } finally {
      setCarregandoMais(false);
    }
  };

  if (loading && itens.length === 0) {
    return <p className="student-screen__estado">Carregando…</p>;
  }

  if (error && itens.length === 0) {
    return (
      <div className="student-screen__estado">
        <p>{erro}</p>
        <button type="button" className="student-app__retry" onClick={() => refetch()}>
          Tentar de novo
        </button>
      </div>
    );
  }

  if (itens.length === 0) {
    return <p className="student-screen__estado">{vazio}</p>;
  }

  return (
    <div className="records">
      {itens.map((item) => {
        const capa = capaDoAlvo(item);
        const quando = dataDeConclusao(item);
        const tipo = tipoLegivel(item.targetType);
        const rota = rotaDoAlvo(item);

        const conteudo = (
          <>
            {capa ? (
              <img className="record-card__imagem" src={capa} alt="" loading="lazy" />
            ) : (
              <span className="record-card__imagem record-card__imagem--vazia" style={{backgroundColor: theme.border}}>
                <i className={`fa-light ${iconeDoTipo(item.targetType)}`} aria-hidden="true" />
              </span>
            )}

            <div className="record-card__info">
              <h2 className="record-card__nome">{rotuloDoAlvo(item)}</h2>
              {tipo && (
                <span className="record-card__categoria" style={{color: theme.neutral}}>
                  {tipo}
                </span>
              )}
            </div>

            {quando && (
              <div className="record-card__marca">
                <span style={{color: theme.neutral}}>{quando}</span>
              </div>
            )}
          </>
        );

        const estilo = {backgroundColor: theme.body, borderColor: theme.border, color: theme.text};

        // Alvo apagado nao vira link: levaria a uma tela de nao encontrado.
        return rota ? (
          <Link key={item.id} to={rota} className="record-card record-card__link" style={estilo}>
            {conteudo}
          </Link>
        ) : (
          <article key={item.id} className="record-card" style={estilo}>
            {conteudo}
          </article>
        );
      })}

      {!acabou && itens.length < total && (
        <button
          type="button"
          className="student-app__retry cursos__mais"
          onClick={carregarMais}
          disabled={carregandoMais}
        >
          {carregandoMais ? 'Carregando…' : 'Ver mais'}
        </button>
      )}
    </div>
  );
};
